<script setup lang="ts">
import { computed } from 'vue'
import { default as PTag } from 'primevue/tag'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'

const props = defineProps<{
  version: QuizVersion
  severity?: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'
  clickable?: boolean
  showQuestionCount?: boolean
}>()

const emits = defineEmits<{
  click: [version: QuizVersion]
}>()

const tagSeverity = computed(() => {
  if (props.severity) return props.severity

  if (props.version.isActive) return 'success'
  if (props.version.isDraft) return 'warn'
  return 'contrast'
})

const questionCount = computed(() => props.version.questionCount)

const circleBackgroundColor = computed(() => {
  // Map severity to text colors that we'll use as background
  const severityToTextColor = {
    success: '#16a34a', // text-green-700
    secondary: '#6b7280', // text-gray-500
    info: '#2563eb', // text-blue-600
    warn: '#d97706', // text-amber-600
    danger: '#dc2626', // text-red-600
    contrast: '#374151' // text-gray-700
  }

  return severityToTextColor[tagSeverity.value] || severityToTextColor.contrast
})

function handleClick() {
  if (props.clickable) {
    emits('click', props.version)
  }
}
</script>

<template>
  <p-tag
    :severity="tagSeverity"
    :class="{ 'cursor-pointer hover:opacity-75': clickable }"
    @click="handleClick"
  >
    Quiz v.{{ version.versionNumber }}
    <div
      v-if="showQuestionCount"
      class="ml-2 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium text-white"
      :style="{ backgroundColor: circleBackgroundColor }"
      :title="`${questionCount} question${questionCount > 1 ? 's' : ''}`"
    >
      {{ questionCount }}
    </div>
  </p-tag>
</template>
